import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useDebounce } from 'use-debounce';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/react';
import apiHelper from '../../utils/apiHelpers';
import cardListParser from '../../utils/cardListParser';
import cardRoles from '../../utils/cardRoles';
import cardUtils from '../../utils/cardUtils';
import deckBuilderStorage from '../../utils/deckBuilderStorage';
import { useCollection } from '../collection/CollectionContext';
import CardImage from '../card/CardImage';
import CommanderDetails from './CommanderDetails';
import DeckPrice from './DeckPrice';
import EdhrecCardList from './EdhrecCardList';
import OnlyCollectionToggle from './OnlyCollectionToggle';
import ChatAdvisor from './ChatAdvisor';
import CardsNotFoundList from '../card/CardsNotFoundList';
import Loader from '../common/Loader';
import Button from '../common/Button';
import Input from '../common/Input';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const TABS = [
  'Lista de cartas',
  'Descripción del mazo',
  'Cartas recomendadas',
  'Combos',
  'Asesoramiento con IA',
];
// Named so that inserting a tab can't silently send a button to the wrong panel.
const TAB = {
  cards: 0,
  description: 1,
  recommendations: 2,
  combos: 3,
  advisor: 4,
};
const ROLE_ORDER = ['land', 'ramp', 'draw', 'removal', 'synergy'];

// Keep the values in step with FORMATS in deckBuilderStorage.js and with DeckFormat on
// the server — they select the card pool the whole screen and the assistant work from.
const DECK_FORMATS = [
  { value: 'commander', label: 'Commander (EDH)' },
  { value: 'brawl', label: 'Brawl (MTG Arena)' },
];

const DeckBuilder = () => {
  const { quantities } = useCollection();
  // Read once, then used to seed the state that survives a browser close.
  const [persisted] = useState(() => deckBuilderStorage.getState());

  const [activeTabIndex, setActiveTabIndex] = useState(
    persisted.activeTabIndex,
  );
  // One per tab, so recommendations and combos filter independently.
  const [onlyCollectionRecommendations, setOnlyCollectionRecommendations] =
    useState(persisted.onlyCollectionRecommendations);
  const [onlyCollectionCombos, setOnlyCollectionCombos] = useState(
    persisted.onlyCollectionCombos,
  );
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [moxfieldUrl, setMoxfieldUrl] = useState('');
  const [loadingMoxfield, setLoadingMoxfield] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  // Its own message: errorMessage is only rendered inside the recommendations
  // and combos panels, so an import failure would surface on the wrong tab.
  const [moxfieldError, setMoxfieldError] = useState('');

  const [commanderName, setCommanderName] = useState(persisted.commanderName);
  const [debouncedCommanderName] = useDebounce(commanderName, 1000);
  const [commander, setCommander] = useState(null);
  const [loadingCommander, setLoadingCommander] = useState(false);

  const [cardlists, setCardlists] = useState([]);
  // Recommander's single list, shaped like an EDHREC one so it renders through the
  // same component, or null when it has nothing. Kept apart from `cardlists` so the
  // two sources load and fail independently.
  const [recommanderList, setRecommanderList] = useState(null);
  const [combos, setCombos] = useState([]);
  const [spellbookCombos, setSpellbookCombos] = useState([]);
  const [cardsNotFound, setCardsNotFound] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [loadingRecommander, setLoadingRecommander] = useState(false);
  const [loadingCombos, setLoadingCombos] = useState(false);
  const [loadingSpellbookCombos, setLoadingSpellbookCombos] = useState(false);
  // The commander each tab's contents were loaded for, or null when it holds
  // nothing. Opening a tab loads it; these keep that from happening again on
  // every visit, and make a commander change reload rather than show stale data.
  const [loadedRecommendationsFor, setLoadedRecommendationsFor] =
    useState(null);
  const [loadedCombosFor, setLoadedCombosFor] = useState(null);
  const [showCards, setShowCards] = useState(persisted.showCards);
  const [errorMessage, setErrorMessage] = useState('');

  // Decides the legal card pool for every search, combo lookup and chat answer.
  const [format, setFormat] = useState(persisted.format);
  const [deckListText, setDeckListText] = useState(persisted.deckListText);
  const [deckDescription, setDeckDescription] = useState(
    persisted.deckDescription,
  );
  // The chat transcript lives here rather than in ChatAdvisor: TabPanel unmounts
  // its children when you switch tabs, which would otherwise destroy the
  // conversation and drop a reply that arrives after you leave.
  const [chatMessages, setChatMessages] = useState(persisted.chatMessages);
  const [debouncedDeckListText] = useDebounce(deckListText, 1000);
  const [deckCards, setDeckCards] = useState([]);
  const [deckCardsNotFound, setDeckCardsNotFound] = useState([]);
  const [loadingDeck, setLoadingDeck] = useState(false);
  // Whether deckCards reflects the current list yet. On a reload the commander
  // resolves first — it's one name against the deck's hundred — so without this the
  // recommendations tab would ask Recommander before the deck had arrived and get
  // back the near-empty answer it gives for an empty list.
  const [deckResolved, setDeckResolved] = useState(false);

  // CardKingdom prices fetched live for cards with no stored price (see the
  // effect below), keyed by card name.
  const [ckPrices, setCkPrices] = useState({});
  const requestedCkNames = useRef(new Set());

  // What Recommander gets asked with. Held in a ref, not read from state inside the
  // loader, so that editing the deck does not retrigger the tab-open effect below:
  // the list is refreshed when you open the tab or press the refresh button, never
  // under your cursor while you add cards from it.
  const recommanderInput = useRef({ format: persisted.format, deckNames: [] });

  const deckCardIds = useMemo(
    () => new Set(deckCards.map(card => card.id)),
    [deckCards],
  );

  // Keyed on the live text, not the debounced one: these quantities drive the
  // deck stepper and the "En el mazo" tag, which would otherwise lag a full
  // second behind every click. deckCards still lags by the debounce, so a card
  // added by the stepper just contributes no price until it resolves.
  const deckQuantities = useMemo(
    () => cardListParser.buildDeckQuantities(deckListText),
    [deckListText],
  );

  // Both recommendation sources render as one stack of lists, so they are merged
  // before filtering rather than after — otherwise the collection toggle and the
  // empty states would each need to know about two sources.
  const allCardlists = useMemo(
    () => (recommanderList ? [...cardlists, recommanderList] : cardlists),
    [cardlists, recommanderList],
  );

  // "Incluir solo cartas en colección" filters what is already on screen, off
  // the collection map, so flipping it needs no refetch.
  const visibleCardlists = useMemo(() => {
    if (!onlyCollectionRecommendations) return allCardlists;
    return allCardlists
      .map(list => ({
        ...list,
        cards: list.cards.filter(card => quantities[card.id] > 0),
      }))
      .filter(list => list.cards.length > 0);
  }, [allCardlists, onlyCollectionRecommendations, quantities]);

  // A combo is only usable if you own *every* card in it — including the ones
  // the server could not resolve, hence the cardCount check.
  const filterCombos = useCallback(
    comboList => {
      if (!onlyCollectionCombos) return comboList;
      return comboList.filter(
        combo =>
          combo.cards.length > 0 &&
          combo.cards.length === combo.cardCount &&
          combo.cards.every(card => quantities[card.id] > 0),
      );
    },
    [onlyCollectionCombos, quantities],
  );

  const visibleCombos = useMemo(
    () => filterCombos(combos),
    [combos, filterCombos],
  );
  const visibleSpellbookCombos = useMemo(
    () => filterCombos(spellbookCombos),
    [spellbookCombos, filterCombos],
  );

  // Written on every change rather than debounced: the point is to survive an
  // abrupt tab close, and a debounced write would lose the last keystrokes. The
  // payload is a few KB.
  useEffect(() => {
    deckBuilderStorage.saveState({
      format,
      commanderName,
      deckListText,
      deckDescription,
      chatMessages,
      activeTabIndex,
      showCards,
      onlyCollectionRecommendations,
      onlyCollectionCombos,
    });
  }, [
    format,
    commanderName,
    deckListText,
    deckDescription,
    chatMessages,
    activeTabIndex,
    showCards,
    onlyCollectionRecommendations,
    onlyCollectionCombos,
  ]);

  useEffect(() => {
    if (debouncedCommanderName.trim().length < 3) {
      setCommander(null);
      return;
    }
    setLoadingCommander(true);
    apiHelper
      .getCardByName(debouncedCommanderName.trim())
      .then(response => {
        setCommander(
          response.data && response.data[0] ? response.data[0] : null,
        );
        setLoadingCommander(false);
      })
      .catch(() => {
        setCommander(null);
        setLoadingCommander(false);
      });
  }, [debouncedCommanderName]);

  useEffect(() => {
    if (!debouncedDeckListText.trim()) {
      setDeckCards([]);
      setDeckCardsNotFound([]);
      setDeckResolved(true);
      return;
    }
    setLoadingDeck(true);
    setDeckResolved(false);
    apiHelper
      .getCardsBulk({
        where: { name: debouncedDeckListText },
        limit: 1000,
        exactMatch: true,
      })
      .then(response => {
        setDeckCards(response.data.cards || []);
        setDeckCardsNotFound(response.data.cardsNotFound || []);
        setLoadingDeck(false);
        setDeckResolved(true);
      })
      .catch(() => {
        setLoadingDeck(false);
        // Settled either way: a failed lookup must not leave the recommendations
        // tab waiting forever for a deck that is never going to arrive.
        setDeckResolved(true);
      });
  }, [debouncedDeckListText]);

  // CardKingdom reference prices are only stored on card_price rows, so cards no
  // tracked store carries have none locally. Look those up in one batched call so
  // the deck totals aren't silently short.
  useEffect(() => {
    const cards = commander ? [...deckCards, commander] : deckCards;
    const missingNames = new Set(
      cards
        .filter(card => cardUtils.cardKingdomPrice(card) === null)
        .map(card => card.name),
    );
    // Only ask for names we haven't asked for yet, so editing the list doesn't
    // re-request the whole deck on every change.
    const names = Array.from(missingNames).filter(
      name => !requestedCkNames.current.has(name),
    );
    if (!names.length) return;

    names.forEach(name => requestedCkNames.current.add(name));

    let cancelled = false;
    apiHelper
      .getCardKingdomPrices(names)
      .then(response => {
        if (cancelled) return;
        setCkPrices(prevPrices => ({
          ...prevPrices,
          ...(response.data.prices || {}),
        }));
      })
      .catch(() => {
        // Let a transient failure be retried the next time the deck changes.
        names.forEach(name => requestedCkNames.current.delete(name));
      });

    return () => {
      cancelled = true;
    };
  }, [deckCards, commander]);

  const handleChange = e => {
    setCommanderName(e.target.value);
  };

  const handleApiError = error => {
    if (error.response) {
      setErrorMessage(
        error.response.data?.message || 'Error al cargar datos de EDHREC',
      );
    } else if (error.request) {
      setErrorMessage('Sin respuesta del Servidor');
    } else {
      setErrorMessage('Error de conexión con el servidor');
    }
  };

  const addCardsNotFound = newCardsNotFound => {
    setCardsNotFound(prevCardsNotFound =>
      Array.from(new Set([...prevCardsNotFound, ...newCardsNotFound])),
    );
  };

  // Opening a tab loads every source that feeds it. `loadedFor` holds the
  // commander the data on screen belongs to, which is what keeps a tab from
  // refetching each time you come back to it, and what makes a new commander
  // refetch on arrival.
  const loadRecommendations = useCallback(commanderCardName => {
    setLoadedRecommendationsFor(commanderCardName);
    setLoadingRecommendations(true);
    setLoadingRecommander(true);
    setErrorMessage('');

    let failed = false;
    // Either source failing clears the marker, so reopening the tab retries the one
    // that failed rather than leaving it permanently missing.
    const finish = () => {
      if (failed) setLoadedRecommendationsFor(null);
    };

    apiHelper
      .getCommanderRecommendations(commanderCardName)
      .then(response => {
        setCardlists(response.data.cardlists || []);
        addCardsNotFound(response.data.cardsNotFound || []);
      })
      .catch(error => {
        setCardlists([]);
        failed = true;
        handleApiError(error);
      })
      .finally(() => {
        setLoadingRecommendations(false);
        finish();
      });

    const { format: recommanderFormat, deckNames } = recommanderInput.current;
    apiHelper
      .getRecommanderRecommendations(
        commanderCardName,
        recommanderFormat,
        deckNames,
      )
      .then(response => {
        const [list] = response.data.cardlists || [];
        setRecommanderList(
          list && list.cards.length
            ? {
                ...list,
                // Their terms ask for attribution; EdhrecCardList renders this as a
                // link in the list header, the same as the combo lists do.
                link: 'https://recommander.cards',
                linkLabel: 'Ver en Recommander',
              }
            : null,
        );
        addCardsNotFound(response.data.cardsNotFound || []);
      })
      .catch(error => {
        setRecommanderList(null);
        // A commander Recommander doesn't know is a 404 and simply means no list —
        // not worth an error banner when EDHREC may still have loaded fine.
        if (!error.response || error.response.status !== 404) {
          failed = true;
          handleApiError(error);
        }
      })
      .finally(() => {
        setLoadingRecommander(false);
        finish();
      });
  }, []);

  const loadCombos = useCallback(commanderCardName => {
    setLoadedCombosFor(commanderCardName);
    setLoadingCombos(true);
    setLoadingSpellbookCombos(true);
    setErrorMessage('');

    let failed = false;
    const onFailure = error => {
      failed = true;
      handleApiError(error);
    };
    // Both sources feed the one tab, so they load together. Either failing
    // clears the marker, since a retry has to cover the source that failed.
    const finish = () => {
      if (failed) setLoadedCombosFor(null);
    };

    apiHelper
      .getCommanderCombos(commanderCardName)
      .then(response => {
        setCombos(response.data.combos || []);
        addCardsNotFound(response.data.cardsNotFound || []);
      })
      .catch(error => {
        setCombos([]);
        onFailure(error);
      })
      .finally(() => {
        setLoadingCombos(false);
        finish();
      });

    apiHelper
      .getCommanderSpellbookCombos(commanderCardName)
      .then(response => {
        setSpellbookCombos(response.data.combos || []);
        addCardsNotFound(response.data.cardsNotFound || []);
      })
      .catch(error => {
        setSpellbookCombos([]);
        onFailure(error);
      })
      .finally(() => {
        setLoadingSpellbookCombos(false);
        finish();
      });
  }, []);

  // A new commander invalidates both tabs: their contents are entirely derived
  // from it. Whichever tab is open reloads through the effect below.
  const commanderCardName = commander ? commander.name : null;
  useEffect(() => {
    setCardlists([]);
    setRecommanderList(null);
    setCombos([]);
    setSpellbookCombos([]);
    setCardsNotFound([]);
    setErrorMessage('');
    setLoadedRecommendationsFor(null);
    setLoadedCombosFor(null);
  }, [commanderCardName]);

  // Kept current for whenever the next Recommander call happens, without making that
  // call a dependency of the tab-open effect. The commander goes in its own field of
  // the request, so it is dropped from the deck here.
  useEffect(() => {
    recommanderInput.current = {
      format,
      deckNames: deckCards
        .map(card => card.name)
        .filter(name => name !== commanderCardName),
    };
  }, [format, deckCards, commanderCardName]);

  // Re-runs both sources against the deck as it stands now. Only Recommander's
  // output can actually change — EDHREC reads the commander alone — but reloading
  // the whole tab is the simpler thing to reason about.
  const handleRefreshRecommendations = () => {
    if (!commanderCardName) return;
    loadRecommendations(commanderCardName);
  };

  // Selecting the tab is the trigger — there are no search buttons. Runs after
  // the reset above on a commander change, so it always loads the current one.
  useEffect(() => {
    if (!commanderCardName) return;

    if (
      activeTabIndex === TAB.recommendations &&
      loadedRecommendationsFor !== commanderCardName &&
      // Recommander is asked with the deck, so wait until there is one to send.
      deckResolved
    ) {
      loadRecommendations(commanderCardName);
    }

    if (
      activeTabIndex === TAB.combos &&
      loadedCombosFor !== commanderCardName
    ) {
      loadCombos(commanderCardName);
    }
  }, [
    activeTabIndex,
    commanderCardName,
    loadedRecommendationsFor,
    loadedCombosFor,
    deckResolved,
    loadRecommendations,
    loadCombos,
  ]);

  const handleAddToDeck = card => {
    if (deckCardIds.has(card.id)) return;
    setDeckListText(prevText =>
      prevText.trim() ? `${prevText}\n1 ${card.name}` : `1 ${card.name}`,
    );
  };

  const handleDeckQuantityChange = (card, quantity) => {
    setDeckListText(prevText =>
      cardListParser.setCardQuantity(prevText, card.name, quantity),
    );
  };

  const handleCopyList = () => {
    const text = cardListParser.buildCopyListText(deckCards, deckQuantities);
    navigator.clipboard.writeText(text);
  };

  const runMoxfieldImport = () => {
    setShowImportConfirm(false);
    setLoadingMoxfield(true);
    setMoxfieldError('');
    apiHelper
      .importMoxfieldDeck(moxfieldUrl.trim())
      .then(response => {
        setDeckListText(response.data.deckListText || '');
        setDeckDescription(response.data.description || '');
        // Only overwrite the commander when Moxfield told us one.
        if (response.data.commanderName) {
          setCommanderName(response.data.commanderName);
        }
        setActiveTabIndex(TAB.cards);
        setLoadingMoxfield(false);
      })
      .catch(error => {
        setLoadingMoxfield(false);
        if (error.response) {
          setMoxfieldError(
            error.response.data?.message ||
              'No pudimos importar el mazo de Moxfield',
          );
        } else if (error.request) {
          setMoxfieldError('Sin respuesta del Servidor');
        } else {
          setMoxfieldError('Error de conexión con el servidor');
        }
      });
  };

  const handleMoxfieldImport = () => {
    if (!moxfieldUrl.trim() || loadingMoxfield) return;
    // Importing replaces the list, so ask before throwing work away.
    if (deckListText.trim()) {
      setShowImportConfirm(true);
      return;
    }
    runMoxfieldImport();
  };

  // Emptying the text is enough for the list itself: the debounced effect above
  // clears deckCards, which empties the Mazo tab's lists and the deck totals.
  //
  // The description goes with it. Both are imported together from Moxfield and both
  // describe one deck, so leaving a stale primer behind after clearing the list would
  // feed the chat assistant a plan for cards that are no longer there. The commander,
  // recommendations and combos are still left alone.
  const handleClearList = () => {
    setDeckListText('');
    setDeckDescription('');
    setShowClearConfirm(false);
  };

  // Opened in a new tab, so it's a plain link rather than a navigate() call.
  const buildFindInStoresUrl = card =>
    `${process.env.PUBLIC_URL}/findcard?name=${encodeURIComponent(card.name)}`;

  return (
    <div className="flex flex-col gap-y-4">
      <h1 className="text-2xl text-center">
        <i className="fas fa-crown mr-2"></i>
        Deck builder
      </h1>
      <h4>
        Armá tu mazo pegando una lista existente o agregando cartas desde cero,
        y usá las recomendaciones y combos de EDHREC y Commander Spellbook para
        encontrarle mejoras. Agregá las cartas directamente a tu wishlist, a tu
        carrito de compras o al mazo.
      </h4>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex flex-col gap-y-4 w-full sm:w-1/2">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              name="commanderName"
              placeholder='Nombre del comandante. Por ejemplo, "Uril, the Miststalker"'
              value={commanderName}
              onChange={handleChange}
            />
            {/* Only wrapped in the anchor once a commander resolved: a disabled
                button inside an href-less link still navigates when the area
                around it is clicked. */}
            {commander ? (
              <a
                href={buildFindInStoresUrl(commander)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-32 shrink-0">
                <Button className="w-full" iconClass="fas fa-store">
                  Comprar
                </Button>
              </a>
            ) : (
              <Button
                className="w-full sm:w-32 shrink-0"
                iconClass="fas fa-store"
                disabled>
                Comprar
              </Button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              name="moxfieldUrl"
              placeholder="URL de un mazo de Moxfield"
              value={moxfieldUrl}
              onChange={e => setMoxfieldUrl(e.target.value)}
            />
            <Button
              className="w-full sm:w-32"
              iconClass="fas fa-file-import"
              onClick={handleMoxfieldImport}
              disabled={!moxfieldUrl.trim() || loadingMoxfield}>
              Importar
            </Button>
          </div>
          {loadingMoxfield && <Loader />}
          <Alert>{moxfieldError}</Alert>
          {/* Decides the legal card pool for every search, combo lookup and chat
              answer on this screen. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label htmlFor="deck-format" className="text-white shrink-0">
              Formato
            </label>
            <select
              id="deck-format"
              name="format"
              value={format}
              onChange={e => setFormat(e.target.value)}
              className="w-full rounded p-2 bg-mediumColor text-white border border-gray-600">
              {DECK_FORMATS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              className="w-full sm:w-1/2"
              iconClass="fas fa-copy"
              onClick={handleCopyList}
              disabled={!deckCards.length}>
              Copiar lista
            </Button>
            <Button
              className="w-full sm:w-1/2"
              iconClass="fas fa-eraser"
              onClick={() => setShowClearConfirm(true)}
              // Enabled when either is set, since it clears both: a description with no
              // list (a Moxfield import whose primer came through but whose list didn't)
              // would otherwise be impossible to clear from here.
              disabled={!deckListText.trim() && !deckDescription.trim()}>
              Limpiar lista
            </Button>
          </div>
          {/* In the left column rather than across the page, so the pair matches
              the width of the two buttons above it. */}
          {(allCardlists.length > 0 ||
            combos.length > 0 ||
            spellbookCombos.length > 0 ||
            deckCards.length > 0) && (
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                className="w-full sm:w-1/2"
                iconClass="fas fa-id-card"
                onClick={() => setShowCards(true)}
                disabled={
                  loadingRecommendations ||
                  loadingRecommander ||
                  loadingCombos ||
                  loadingSpellbookCombos ||
                  loadingDeck
                }>
                Ver en grilla
              </Button>
              <Button
                className="w-full sm:w-1/2"
                iconClass="fas fa-list-alt"
                onClick={() => setShowCards(false)}
                disabled={
                  loadingRecommendations ||
                  loadingRecommander ||
                  loadingCombos ||
                  loadingSpellbookCombos ||
                  loadingDeck
                }>
                Ver en lista
              </Button>
            </div>
          )}
        </div>

        <div className="w-full sm:w-1/2 flex flex-col sm:flex-row items-start gap-4">
          {loadingCommander && <Loader />}
          {!loadingCommander && commander && (
            <div className="w-48 shrink-0">
              <CardImage card={commander} />
            </div>
          )}
          <div className="flex-1 min-w-0 w-full flex flex-col gap-y-3">
            {!loadingCommander && commander && (
              <CommanderDetails
                commander={commander}
                cardKingdomPrice={
                  cardUtils.cardKingdomPrice(commander) ??
                  ckPrices[commander.name]
                }
              />
            )}
            {deckCards.length > 0 && (
              <DeckPrice
                deckCards={deckCards}
                quantities={deckQuantities}
                ckPrices={ckPrices}
              />
            )}
          </div>
        </div>
      </div>

      <TabGroup selectedIndex={activeTabIndex} onChange={setActiveTabIndex}>
        <TabList className="flex flex-row gap-2 border-b border-gray-600">
          {TABS.map(label => (
            <Tab
              key={label}
              className={({ selected }) =>
                `px-4 py-2 font-semibold outline-none ${
                  selected
                    ? 'border-b-2 border-accentDark text-accentDark'
                    : 'text-gray-400'
                }`
              }>
              {label}
            </Tab>
          ))}
        </TabList>
        <TabPanels className="flex flex-col gap-y-4 mt-4">
          <TabPanel className="flex flex-col gap-y-4">
            <Input
              type="textarea"
              name="deckListText"
              placeholder='Pegá tu lista de cartas (formato findbulk o Moxfield), o agregá cartas desde "Cartas recomendadas" o "Combos"'
              value={deckListText}
              onChange={e => setDeckListText(e.target.value)}
            />
            {loadingDeck && <Loader />}
            {!loadingDeck && deckCards.length === 0 && (
              <p className="text-gray-400">Todavía no hay cartas en el mazo.</p>
            )}
            {!loadingDeck && (
              <EdhrecCardList
                title="Cartas en el mazo"
                cards={deckCards}
                showCards={showCards}
                deckQuantities={deckQuantities}
                countCopies
                onDeckQuantityChange={handleDeckQuantityChange}
              />
            )}
            {!loadingDeck &&
              ROLE_ORDER.map(role => (
                <EdhrecCardList
                  key={role}
                  title={cardRoles.ROLE_LABELS[role]}
                  cards={deckCards.filter(
                    card => card.roles && card.roles.includes(role),
                  )}
                  showCards={showCards}
                  deckQuantities={deckQuantities}
                  countCopies
                  onDeckQuantityChange={handleDeckQuantityChange}
                />
              ))}
            {!loadingDeck && deckCardsNotFound.length > 0 && (
              <CardsNotFoundList cardsNotFound={deckCardsNotFound} />
            )}
          </TabPanel>

          <TabPanel className="flex flex-col gap-y-4">
            <h4>
              La descripción y el primer del mazo. Se importan desde Moxfield y
              podés editarlos. El asistente de "Asesoramiento" los lee para
              entender el plan del mazo.
            </h4>
            <Input
              type="textarea"
              name="deckDescription"
              placeholder="Describí la estrategia del mazo, sus combos y cómo gana."
              value={deckDescription}
              onChange={e => setDeckDescription(e.target.value)}
            />
          </TabPanel>

          <TabPanel className="flex flex-col gap-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <OnlyCollectionToggle
                checked={onlyCollectionRecommendations}
                onChange={setOnlyCollectionRecommendations}
              />
              {/* EDHREC reads the commander alone, but Recommander reads the deck,
                  and the tab deliberately doesn't refetch as you edit — so there has
                  to be a way to ask again with the list as it stands now. */}
              <Button
                className="w-full sm:w-auto sm:shrink-0"
                iconClass="fas fa-sync"
                onClick={handleRefreshRecommendations}
                disabled={
                  !commander || loadingRecommendations || loadingRecommander
                }>
                Actualizar con mi mazo actual
              </Button>
            </div>
            {(loadingRecommendations || loadingRecommander) && <Loader />}
            {!loadingRecommendations && !loadingRecommander && (
              <Alert>{errorMessage}</Alert>
            )}
            {!loadingRecommendations &&
              !loadingRecommander &&
              visibleCardlists.map(list => (
                <EdhrecCardList
                  key={list.tag}
                  title={list.header}
                  cards={list.cards}
                  link={list.link}
                  linkLabel={list.linkLabel}
                  showCards={showCards}
                  deckQuantities={deckQuantities}
                  onAddToDeck={handleAddToDeck}
                />
              ))}
            {!loadingRecommendations &&
              !loadingRecommander &&
              allCardlists.length === 0 && (
                <p className="text-gray-400">
                  {commander
                    ? 'No hay recomendaciones para este comandante.'
                    : 'Ingresá un comandante para ver sus cartas recomendadas.'}
                </p>
              )}
            {/* Recommander scores against the deck, so a nearly empty list has
                nothing clearing its threshold. Say that, rather than leaving the
                impression it had nothing to suggest. */}
            {!loadingRecommendations &&
              !loadingRecommander &&
              commander &&
              !recommanderList &&
              cardlists.length > 0 && (
                <p className="text-gray-400">
                  Recommander recomienda en base a las cartas que ya tenés en el
                  mazo: agregá algunas y volvé a tocar "Actualizar con mi mazo
                  actual" para ver sus sugerencias.
                </p>
              )}
            {!loadingRecommendations &&
              !loadingRecommander &&
              allCardlists.length > 0 &&
              visibleCardlists.length === 0 && (
                <p className="text-gray-400">
                  Ninguna de las cartas recomendadas está en tu colección.
                  Desactivá "Incluir solo cartas en colección" para verlas
                  todas.
                </p>
              )}
            {!loadingRecommendations &&
              !loadingRecommander &&
              cardsNotFound.length > 0 && (
                <CardsNotFoundList cardsNotFound={cardsNotFound} />
              )}
          </TabPanel>

          <TabPanel className="flex flex-col gap-y-4">
            <OnlyCollectionToggle
              checked={onlyCollectionCombos}
              onChange={setOnlyCollectionCombos}
            />
            {(loadingCombos || loadingSpellbookCombos) && <Loader />}
            {!loadingCombos && !loadingSpellbookCombos && (
              <Alert>{errorMessage}</Alert>
            )}
            {!loadingCombos &&
              visibleCombos.map((combo, index) => (
                <EdhrecCardList
                  key={combo.url || index}
                  title={combo.title}
                  cards={combo.cards}
                  link={combo.url}
                  linkLabel="Ver en EDHREC"
                  showCards={showCards}
                  deckQuantities={deckQuantities}
                  onAddToDeck={handleAddToDeck}
                />
              ))}
            {!loadingSpellbookCombos &&
              visibleSpellbookCombos.map(combo => (
                <EdhrecCardList
                  key={combo.id}
                  title={combo.title}
                  cards={combo.cards}
                  link={combo.url}
                  linkLabel="Ver en Commander Spellbook"
                  showCards={showCards}
                  deckQuantities={deckQuantities}
                  onAddToDeck={handleAddToDeck}
                />
              ))}
            {!loadingCombos &&
              !loadingSpellbookCombos &&
              combos.length + spellbookCombos.length === 0 && (
                <p className="text-gray-400">
                  {commander
                    ? 'No encontramos combos para este comandante.'
                    : 'Ingresá un comandante para ver sus combos.'}
                </p>
              )}
            {!loadingCombos &&
              !loadingSpellbookCombos &&
              combos.length + spellbookCombos.length > 0 &&
              visibleCombos.length + visibleSpellbookCombos.length === 0 && (
                <p className="text-gray-400">
                  Ningún combo tiene todas sus cartas en tu colección. Desactivá
                  "Incluir solo cartas en colección" para verlos todos.
                </p>
              )}
            {!loadingCombos &&
              !loadingSpellbookCombos &&
              cardsNotFound.length > 0 && (
                <CardsNotFoundList cardsNotFound={cardsNotFound} />
              )}
          </TabPanel>

          {/* unmount={false} keeps the draft input, the scroll position and the
              "typing" indicator alive across tab switches — the transcript
              itself is held above, in chatMessages. */}
          <TabPanel className="flex flex-col gap-y-4" unmount={false}>
            <ChatAdvisor
              commanderName={commander ? commander.name : ''}
              format={format}
              deckListText={deckListText}
              deckDescription={deckDescription}
              messages={chatMessages}
              onMessagesChange={setChatMessages}
            />
          </TabPanel>
        </TabPanels>
      </TabGroup>

      <ConfirmDialog
        open={showImportConfirm}
        title="Importar desde Moxfield"
        message="Ya tenés cartas en la lista. Importar este mazo va a reemplazar la lista, el comandante y la descripción."
        confirmLabel="Importar y reemplazar"
        confirmIconClass="fas fa-file-import"
        onConfirm={runMoxfieldImport}
        onClose={() => setShowImportConfirm(false)}
      />

      <ConfirmDialog
        open={showClearConfirm}
        title="Limpiar lista"
        message="Se van a vaciar la lista de cartas y la descripción del mazo. El comandante, las recomendaciones y los combos se mantienen."
        confirmLabel="Limpiar lista"
        confirmIconClass="fas fa-eraser"
        onConfirm={handleClearList}
        onClose={() => setShowClearConfirm(false)}
      />
    </div>
  );
};

export default DeckBuilder;
